import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["step", "nextButton", "countdownContainer", "recapContent"]
  static values = {
    currentStep: Number,
    showcaseType: String,
    accessToken: String
  }

  connect() {
    this.currentStepValue = 1
    this.updateNavigation()
    this.trackEvent('step_viewed', { step: this.currentStepValue })
    this.showWelcomeModal()
  }

  showWelcomeModal() {
    const modalElement = document.getElementById('showcaseWelcomeModal')
    if (modalElement) {
      // Use Bootstrap 5 Modal API
      const modal = new bootstrap.Modal(modalElement)
      modal.show()

      // Track the modal view
      this.trackEvent('welcome_modal_viewed', { step: 0 })

      // Track when they click "Let's Go"
      modalElement.addEventListener('hidden.bs.modal', () => {
        this.trackEvent('welcome_modal_dismissed', { step: 0 })
      }, { once: true })
    }
  }

  nextStep() {
    if (this.currentStepValue < 5) {
      this.currentStepValue += 1
      this.showStep(this.currentStepValue)
      this.updateNavigation()
      this.trackEvent('step_viewed', { step: this.currentStepValue })
    }
  }

  previousStep() {
    if (this.currentStepValue > 1) {
      this.currentStepValue -= 1
      this.showStep(this.currentStepValue)
      this.updateNavigation()
      this.trackEvent('step_viewed', { step: this.currentStepValue })
    }
  }

  goToStep(event) {
    const step = event.params.step || event.currentTarget.dataset.step
    if (step && step >= 1 && step <= 5) {
      this.currentStepValue = parseInt(step)
      this.showStep(this.currentStepValue)
      this.updateNavigation()
      this.trackEvent('step_viewed', { step: this.currentStepValue })
    }
  }

  showStep(stepNumber) {
    // Hide all steps
    this.stepTargets.forEach(step => {
      step.classList.remove('active')
    })

    // Show current step
    const currentStepElement = this.stepTargets.find(step =>
      parseInt(step.dataset.step) === stepNumber
    )
    if (currentStepElement) {
      currentStepElement.classList.add('active')
    }

    // Handle special step 5 behavior
    if (stepNumber === 5) {
      this.fetchAndDisplayRecap()
    }
  }

  updateNavigation() {
    // Update step indicators
    document.querySelectorAll('.step-indicator').forEach((indicator, index) => {
      const stepNumber = index + 1
      indicator.classList.remove('active', 'completed')

      if (stepNumber === this.currentStepValue) {
        indicator.classList.add('active')
      } else if (stepNumber < this.currentStepValue) {
        indicator.classList.add('completed')
      }
    })

  }

  startCountdown() {
    console.log('STARTING COUTDNWON FROM the navigation controller');

    // Find the countdown container and start the countdown
    if (this.hasCountdownContainerTarget && this.hasRecapContentTarget) {
      // Show countdown, hide content
      this.countdownContainerTarget.style.display = 'block'
      this.recapContentTarget.style.display = 'none'

      // Start the countdown
      let timeLeft = 3000 // 3 seconds
      const countdownNumber = this.countdownContainerTarget.querySelector('.countdown-number')
      const countdownText = this.countdownContainerTarget.querySelector('.countdown-text')

      if (countdownText) {
        countdownText.textContent = 'Generating your recap...'
      }

      const countdownInterval = setInterval(() => {
        timeLeft -= 100

        if (countdownNumber) {
          const seconds = Math.ceil(timeLeft / 1000)
          countdownNumber.textContent = Math.max(1, seconds)
        }

        if (timeLeft <= 0) {
          clearInterval(countdownInterval)
          this.revealRecap(this.countdownContainerTarget, this.recapContentTarget)
        }
      }, 100)
    } else {
      console.log('No countdown container or recap content found')
    }
  }

  revealRecap(countdownContainer, recapContent) {
    // Hide countdown
    this.countdownContainerTarget.style.display = 'none'

    // Show recap content with fade-in effect
    this.recapContentTarget.style.display = 'block'
    this.recapContentTarget.style.opacity = '0'
    this.recapContentTarget.style.transition = 'opacity 0.5s ease-in'

    // Trigger fade-in
    setTimeout(() => {
      this.recapContentTarget.style.opacity = '1'
    }, 50)

    // Track completion
    this.trackEvent('recap_generated', { step: 5, showcase_type: 'recaps' })
  }

  fetchAndDisplayRecap() {
    // Start the countdown first
    this.startCountdown()

    // Get selections from the selection controller
    const selectionController = this.application.getControllerForElementAndIdentifier(
      document.querySelector('[data-controller~="showcase-selection"]'),
      'showcase-selection'
    )

    if (!selectionController) {
      console.error('Selection controller not found')
      return
    }

    const selections = selectionController.selections
    const { folder, filename } = this.constructFilename(selections)

    console.log('Fetching recap with folder:', folder, 'and filename:', filename)
    console.log('Selections:', selections)

    // Fetch the recap content
    fetch(`/showcases/${this.showcaseTypeValue}/recap_content?folder=${folder}&filename=${filename}`)
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to fetch recap content')
        }
        return response.json()
      })
      .then(data => {
        this.displayRecapContent(data.content)
      })
      .catch(error => {
        console.error('Error fetching recap:', error)
        this.displayRecapContent('Unable to load recap content. Please try again.')
      })
  }

  constructFilename(selections) {
    // Format: {sport_slug}_{layout}_{quote_index}_{game_notes_flag}.txt
    const sport = selections.sport || 'mens_basketball'

    // Map layout index to layout type
    const layoutMap = { 0: 'bullet', 1: 'text' }
    const layout = layoutMap[selections.layout] || 'bullet'

    // Quote index (0 or 1)
    const quote = selections.quote || 0

    // Calculate bitwise flag for game notes
    let notesFlag = 0
    if (selections.notes && selections.notes.length > 0) {
      selections.notes.forEach(noteIndex => {
        // Convert string to integer if needed
        const index = parseInt(noteIndex)
        // Add power of 2 based on index (0 = 1, 1 = 2, etc.)
        notesFlag += Math.pow(2, index)
      })
    } else {
      // Default to 1 if no notes selected
      notesFlag = 1
    }

    return {
      folder: sport,
      filename: `${layout}_${quote}_${notesFlag}`
    }
  }

  displayRecapContent(content) {
    // Find the recap body element and insert the content
    const recapBody = document.querySelector('.recap-body')
    if (recapBody) {
      // Format the content (preserve line breaks)
      const formattedContent = content.replace(/\n\n/g, '</p><p>').replace(/\n/g, '<br>')
      recapBody.innerHTML = `<p>${formattedContent}</p>`
    }
  }

  trackEvent(eventType, metadata = {}) {
    if (!this.accessTokenValue) return

    const payload = {
      event_type: eventType,
      page_name: `step_${this.currentStepValue}`,
      metadata: {
        step: this.currentStepValue,
        showcase_type: this.showcaseTypeValue,
        ...metadata
      }
    }

    fetch(`/showcases/${this.showcaseTypeValue}/events`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify({
        access_token: this.accessTokenValue,
        ...payload
      })
    }).catch(error => {
      console.log('Tracking event failed:', error)
    })
  }
}
